import { Controller } from "@hotwired/stimulus"
import { captureClientEvent } from "../posthog"

let turboVisitStartedAt = null
document.addEventListener("turbo:before-visit", () => { turboVisitStartedAt = performance.now() })

// Samples browser-visible dashboard milestones without turning Turbo frame
// requests into pageviews. Server timing covers Rails; these marks cover what a
// member actually waits for (root, eager frames, market, and LCP).
export default class extends Controller {
  static values = { sampleRate: { type: Number, default: 0.1 } }

  connect() {
    this.sampled = Math.random() < this.sampleRateValue
    if (!this.sampled) return

    this.startedAt = turboVisitStartedAt ?? 0
    turboVisitStartedAt = null
    this.onFrameLoad = this.onFrameLoad.bind(this)
    this.element.addEventListener("turbo:frame-load", this.onFrameLoad)
    this.onFrameState = this.onFrameState.bind(this)
    this.element.addEventListener("dashboard-frame-state:failed", this.onFrameState)
    this.element.addEventListener("dashboard-frame-state:pending-timeout", this.onFrameState)
    this.element.addEventListener("dashboard-frame-state:retry-success", this.onFrameState)
    this.element.addEventListener("dashboard-frame-state:stale-refresh", this.onFrameState)
    this.capture("dashboard_root_complete")
    const briefing = this.element.querySelector("turbo-frame#portfolio_briefing")
    if (briefing && !briefing.querySelector("[data-dashboard-frame-pending], [data-dashboard-frame-refreshing]")) {
      this.capture("dashboard_briefing_complete")
    }

    if (window.PerformanceObserver) {
      this.lcpObserver = new PerformanceObserver((list) => {
        const entry = list.getEntries().at(-1)
        if (entry && entry.startTime >= this.startedAt) this.lastLcp = entry.startTime - this.startedAt
      })
      try { this.lcpObserver.observe({ type: "largest-contentful-paint", buffered: true }) } catch (_e) {}

      this.cls = 0
      this.clsObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (!entry.hadRecentInput && entry.startTime >= this.startedAt) this.cls += entry.value
        })
      })
      try { this.clsObserver.observe({ type: "layout-shift", buffered: true }) } catch (_e) {}
    }
    this.onVisibility = () => {
      if (document.visibilityState === "hidden") {
        if (this.lastLcp) this.capture("dashboard_lcp", { duration_ms: Math.round(this.lastLcp) })
        this.capture("dashboard_cls", { value: Number((this.cls || 0).toFixed(4)) })
        this.lastLcp = null
      }
    }
    document.addEventListener("visibilitychange", this.onVisibility)
  }

  disconnect() {
    if (!this.sampled) return
    this.element.removeEventListener("turbo:frame-load", this.onFrameLoad)
    this.element.removeEventListener("dashboard-frame-state:failed", this.onFrameState)
    this.element.removeEventListener("dashboard-frame-state:pending-timeout", this.onFrameState)
    this.element.removeEventListener("dashboard-frame-state:retry-success", this.onFrameState)
    this.element.removeEventListener("dashboard-frame-state:stale-refresh", this.onFrameState)
    document.removeEventListener("visibilitychange", this.onVisibility)
    if (this.lastLcp) this.capture("dashboard_lcp", { duration_ms: Math.round(this.lastLcp) })
    this.capture("dashboard_cls", { value: Number((this.cls || 0).toFixed(4)) })
    this.lcpObserver?.disconnect()
    this.clsObserver?.disconnect()
  }

  onFrameLoad(event) {
    const id = event.target?.id
    const names = {
      dashboard_overview: "dashboard_overview_complete",
      portfolio_briefing: "dashboard_briefing_complete",
      dashboard_market: "dashboard_market_complete",
    }
    if (names[id] && !event.target.querySelector("[data-dashboard-frame-pending]")) this.capture(names[id])
  }

  onFrameState(event) {
    const names = {
      "dashboard-frame-state:failed": "dashboard_frame_failed",
      "dashboard-frame-state:pending-timeout": "dashboard_pending_timeout",
      "dashboard-frame-state:retry-success": "dashboard_retry_success",
      "dashboard-frame-state:stale-refresh": "dashboard_stale_refresh",
    }
    if (names[event.type]) this.capture(names[event.type], event.detail || {})
  }

  capture(name, properties = {}) {
    captureClientEvent(name, {
      duration_ms: Math.round(performance.now() - this.startedAt),
      ...properties,
    })
  }
}
